import type { StoredCalendarEvent } from '@packages/types';
import {
  addDaysLocal,
  formatDayHeader,
  getLocalDayKey,
  groupItemsByDay,
  todayLocalDayKey,
  type DayGroup as GroupByDayDayGroup,
} from '../lib/group-by-day';

// Re-export the local-day helpers so existing calendar consumers keep working
// without churn. The helpers themselves now live in `lib/group-by-day.ts`
// because the emails page uses the same set.
export { addDaysLocal, formatDayHeader, getLocalDayKey, todayLocalDayKey };

/**
 * Formatted start time and duration for an event row.
 * All-day events render `--` for the start time and `'All day'` for duration
 * so the row keeps its left-column shape.
 */
export function formatEventTimeRange(event: Pick<StoredCalendarEvent, 'startAt' | 'endAt' | 'isAllDay'>): {
  start: string;
  duration: string;
} {
  if (event.isAllDay) {
    return { start: '-', duration: 'All day' };
  }
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  const startStr = start.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const durationMs = Math.max(0, end.getTime() - start.getTime());
  const totalMinutes = Math.round(durationMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  let duration: string;
  if (hours === 0) {
    duration = `${minutes}m`;
  } else if (minutes === 0) {
    duration = `${hours}h`;
  } else {
    duration = `${hours}h ${String(minutes).padStart(2, '0')}`;
  }
  return { start: startStr, duration };
}

/**
 * When the event runs, in full, for the detail panel's header line: the twin of
 * `formatEmailDetailDate`, so the two panels open with the same kind of
 * sentence. `Thu, 9 Apr 2026, 14:32 - 15:30`, or `Thu, 9 Apr 2026, all day`.
 *
 * The row shows only the start and the duration, because a list is scanned;
 * the panel is read, so it spells the window out.
 */
export function formatEventDetailWhen(
  event: Pick<StoredCalendarEvent, 'startAt' | 'endAt' | 'isAllDay'>,
): string {
  const start = new Date(event.startAt);
  const day = start.toLocaleDateString('en-ZA', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  if (event.isAllDay) return `${day}, all day`;
  const time = (value: Date) =>
    value.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}, ${time(start)} - ${time(new Date(event.endAt))}`;
}

export interface DayGroup<E extends StoredCalendarEvent> {
  dayKey: string;
  label: string;
  events: E[];
}

/**
 * Group events by their local-day calendar key. Days are returned newest first
 * (descending by dayKey); within a day, events are returned chronologically
 * (ascending by startAt) so a viewer reads down through the day.
 *
 * Thin wrapper over the shared `groupItemsByDay` helper, kept for source
 * compatibility - legacy `events` field in the DayGroup shape mirrors the
 * generic `items` field of the shared helper.
 */
export function groupEventsByDay<E extends StoredCalendarEvent>(
  events: E[],
  now: Date = new Date(),
): DayGroup<E>[] {
  const groups: GroupByDayDayGroup<E>[] = groupItemsByDay(
    events,
    (event) => event.startAt,
    { withinDayDirection: 'asc', now },
  );
  return groups.map((g) => ({ dayKey: g.dayKey, label: g.label, events: g.items }));
}
